import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Select, DatePicker, Space, Table, Tag, Row, Col, Card, message, Segmented, Button, Drawer,
} from 'antd';
import ResizableTitle from '../components/ResizableTitle';
import { UnorderedListOutlined, AppstoreOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, LineChart, Line, Legend, LabelList,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../api';
import { SPACER_COL, STICKY_OFFSET } from '../utils/tableLayout';

const { RangePicker } = DatePicker;

/**
 * `embedded` renders this page's content without its own heading or filter
 * controls, scoped by the campaign and window passed in. The Campaign Summary
 * uses it so there is one implementation of these numbers rather than two that
 * can drift apart.
 */
export default function ErrorAnalyticsPage({ embedded = false, campaignId = null, from = null, to = null }) {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [colWidths, setColWidths] = useState({});

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });
  const [filterOpts, setFilterOpts] = useState({ campaigns: [], forms: [] });
  const [campaign,  setCampaign]  = useState(embedded && campaignId ? [campaignId] : []);
  const [formId,    setFormId]    = useState(null);
  const [dateRange, setDateRange] = useState(
    embedded && from && to
      ? [dayjs(from), dayjs(to)]
      : [dayjs().subtract(1, 'month').startOf('month'), dayjs()]
  );

  // Embedded: follow the page above when it changes campaign or window.
  useEffect(() => {
    if (!embedded) return;
    setCampaign(campaignId ? [campaignId] : []);
    if (from && to) setDateRange([dayjs(from), dayjs(to)]);
  }, [embedded, campaignId, from, to]);
  const [questionMode, setQuestionMode] = useState('summary');

  // Side panel: the submissions behind an Errors by Section or Errors by
  // Question number, on the page's filters. { section, question, kind:
  // 'errors' | 'feedbacks', period, rows }. A pivot cell passes its week or
  // month (period: { from, to, label }), trimmed to the page's dates.
  const [drill, setDrill] = useState(null);
  const openDrill = (section, kind, period = null, question = null) => {
    const token = {};
    setDrill({ section, question, kind, period, token, rows: null });
    const params = { drill_section: section, drill_kind: kind };
    if (question) params.drill_question = question;
    if (campaign.length) params.campaign   = campaign.join(',');
    if (formId)          params.form_name  = formId;
    let from = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : null;
    let to   = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : null;
    if (period) {
      from = from && from > period.from ? from : period.from;
      to   = to && to < period.to ? to : period.to;
    }
    if (from) params.start_date = from;
    if (to)   params.end_date   = to;
    api.get('/api/errors/analytics', { params })
      .then((r) => setDrill((d) => (d && d.token === token ? { ...d, rows: r.data || [] } : d)))
      .catch(() => { message.error('Failed to load the submissions.'); setDrill(null); });
  };

  // Exports reflect whichever variant is on screen. The pivots are built inside
  // the render, so these rebuild them from the same by_question_month rows
  // rather than the render being refactored to hand its arrays out.
  const monthLabel = (mk) =>
    new Date(mk + '-02').toLocaleString('default', { month: 'short', year: 'numeric' });

  // By Month or By Week pivot: the same rows, keyed by month (YYYY-MM) or by
  // the Monday of the week (YYYY-MM-DD).
  const isWeek  = questionMode === 'week_pivot';
  const isPivot = questionMode === 'month_pivot' || isWeek;
  const pivotRows = () => (isWeek ? data?.by_question_week : data?.by_question_month) || [];
  const pKey = (r) => (isWeek ? r.WeekKey : r.MonthKey);
  // Monday to Sunday, trimmed to the dates searched so a partial week at
  // either end says which days it covers: "Oct 1–5", "Oct 6–12", "Sep 29–Oct 5".
  const weekLabel = (wk) => {
    let a = dayjs(wk); let b = a.add(6, 'day');
    if (dateRange?.[0] && a.isBefore(dateRange[0], 'day')) a = dateRange[0];
    if (dateRange?.[1] && b.isAfter(dateRange[1], 'day')) b = dateRange[1];
    if (a.isSame(b, 'day')) return a.format('MMM D');
    return `${a.format('MMM D')}–${b.format(a.isSame(b, 'month') ? 'D' : 'MMM D')}`;
  };
  const pLabel = (k) => (isWeek ? weekLabel(k) : monthLabel(k));
  const pivotMonths = () => [...new Set(pivotRows().map(pKey))].sort();

  const saveSheet = (rows, sheet, file) => {
    if (!rows.length) { message.info('Nothing to export.'); return; }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheet);
    XLSX.writeFile(wb, file);
  };

  const exportSections = () => {
    if (isPivot) {
      const months = pivotMonths();
      const bySec = {};
      pivotRows().forEach((r) => {
        bySec[r.Category] = bySec[r.Category] || { Section: r.Category };
        bySec[r.Category][pLabel(pKey(r))] =
          (bySec[r.Category][pLabel(pKey(r))] || 0) + r.TotalErrors;
      });
      // Every month present gets a column, so a section with no errors in a
      // month reads as 0 rather than the column vanishing for that row.
      const rows = Object.values(bySec).map((row) => {
        months.forEach((mk) => { row[pLabel(mk)] = row[pLabel(mk)] || 0; });
        return row;
      });
      saveSheet(rows, 'Errors by Section', `errors-by-section-by-${isWeek ? 'week' : 'month'}.xlsx`);
    } else {
      saveSheet((data?.by_category || []).map((r) => ({
        Section:             r.Category,
        'Section Errors':    r.TotalErrors,
        'Section Feedbacks': r.TotalFeedbacks,
      })), 'Errors by Section', 'errors-by-section.xlsx');
    }
  };

  const exportQuestions = () => {
    if (isPivot) {
      const months = pivotMonths();
      const byQ = {};
      pivotRows().forEach((r) => {
        const k = `${r.Category}||${r.QuestionText}`;
        byQ[k] = byQ[k] || { Section: r.Category, Question: r.QuestionText };
        byQ[k][pLabel(pKey(r))] = (byQ[k][pLabel(pKey(r))] || 0) + (r.TotalErrors || 0);
      });
      const rows = Object.values(byQ).map((row) => {
        months.forEach((mk) => { row[pLabel(mk)] = row[pLabel(mk)] || 0; });
        return row;
      });
      saveSheet(rows, 'Errors by Question', `errors-by-question-by-${isWeek ? 'week' : 'month'}.xlsx`);
    } else {
      saveSheet((data?.by_question || []).map((r) => ({
        Section:            r.Category,
        Question:           r.QuestionText,
        '# Submissions':    r.SubmissionCount,
        'Total Errors':     r.TotalErrors,
        'Total Feedbacks':  r.TotalFeedbacks,
      })), 'Errors by Question', 'errors-by-question.xlsx');
    }
  };

  useEffect(() => {
    api.get('/api/errors/filter-options')
      .then((r) => setFilterOpts(r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (campaign.length)  params.campaign   = campaign.join(',');
      if (formId)           params.form_name  = formId;
      if (dateRange?.[0])   params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1])   params.end_date   = dateRange[1].format('YYYY-MM-DD');
      const res = await api.get('/api/errors/analytics', { params });
      setData(res.data);
    } catch {
      message.error('Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, [campaign, formId, dateRange]);

  useEffect(() => { load(); }, [load]);

  const questionColumns = [
    { title: 'Section',       dataIndex: 'Category',       key: 'Category',       width: 160, ellipsis: true },
    { title: 'Question',      dataIndex: 'QuestionText',   key: 'QuestionText',   width: 360, ellipsis: true },
    { title: '# Submissions', dataIndex: 'SubmissionCount',key: 'SubmissionCount',width: 130, align: 'right' },
    {
      title: 'Total Errors', dataIndex: 'TotalErrors', key: 'TotalErrors', width: 120, align: 'right',
      render: (v, r) => (v > 0
        ? <Tag color="red" style={{ cursor: 'pointer' }} onClick={() => openDrill(r.Category, 'errors', null, r.QuestionText)}>{v}</Tag>
        : <Tag color="red">{v}</Tag>),
      sorter: (a, b) => (a.TotalErrors ?? 0) - (b.TotalErrors ?? 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Total Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 135, align: 'right',
      render: (v, r) => (v > 0
        ? <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => openDrill(r.Category, 'feedbacks', null, r.QuestionText)}>{v}</Tag>
        : <span style={{ color: '#bfbfbf' }}>—</span>),
      sorter: (a, b) => (a.TotalFeedbacks ?? 0) - (b.TotalFeedbacks ?? 0),
    },
    SPACER_COL,
  ];

  return (
    <>
      {!embedded && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Error Analytics</Typography.Title>
      </div>
      )}

      {/* The form filter stays when embedded: it narrows within the campaign
          rather than duplicating what the page above already chose. The
          dates do not: the page above's window is the one (a second picker
          here was lost on switching to Insights). */}
      <Space wrap style={{ marginBottom: 24 }}>
        {!embedded && (
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="All campaigns"
          options={filterOpts.campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
          value={campaign}
          onChange={setCampaign}
          maxTagCount="responsive"
          style={{ minWidth: 260 }}
        />
        )}
        <Select
          allowClear
          placeholder="All forms"
          options={filterOpts.forms.map((f) => ({ label: f.name, value: f.name }))}
          value={formId}
          onChange={setFormId}
          style={{ minWidth: 280 }}
        />
        {!embedded && (
        <RangePicker
          placeholder={['Date From', 'Date To']}
          value={dateRange}
          onChange={setDateRange}
          format="YYYY-MM-DD"
        />
        )}
      </Space>

      <Row gutter={[16, 16]}>
        {/* Errors by section */}
        <Col xs={24} lg={12}>
          <Card title="Errors by Section" size="small" loading={loading}>
            {data?.by_category?.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(260, data.by_category.length * 28 + 20)}>
                <BarChart
                  data={data.by_category}
                  layout="vertical"
                  margin={{ top: 4, right: 40, bottom: 4, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="Category" type="category" width={170} tick={{ fontSize: 11 }} interval={0} />
                  <RTooltip />
                  <Bar dataKey="TotalErrors" fill="#ff4d4f" name="Section Errors" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography.Text type="secondary">No data available.</Typography.Text>
            )}
          </Card>
        </Col>

        {/* Section errors by agent */}
        <Col xs={24} lg={12}>
          <Card title="Section Errors by Agent (Top 20)" size="small" loading={loading}>
            {data?.by_agent?.length > 0 ? (
              // Tall enough for a name on every bar (a fixed 300px left room
              // for only every other one, and recharts silently dropped the
              // rest); interval={0} keeps them all.
              <ResponsiveContainer width="100%" height={Math.max(260, Math.min(data.by_agent.length, 20) * 28 + 40)}>
                <BarChart
                  data={data.by_agent.slice(0, 20)}
                  margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="AgentName" type="category" width={140} tick={{ fontSize: 11 }} interval={0} />
                  <RTooltip />
                  <Bar dataKey="TotalErrors" fill="#1677ff" name="Total Errors" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography.Text type="secondary">No data available.</Typography.Text>
            )}
          </Card>
        </Col>

        {/* Errors over time */}
        <Col xs={24}>
          <Card title="Section Errors Over Time" size="small" loading={loading}>
            {data?.by_date?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.by_date} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <RTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="TotalErrors" stroke="#ff4d4f" name="Section Errors" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography.Text type="secondary">No data available.</Typography.Text>
            )}
          </Card>
        </Col>

        {/* Errors by month: the daily figures added up per month. */}
        <Col xs={24}>
          <Card title="Section Errors by Month" size="small" loading={loading}>
            {data?.by_date?.length > 0 ? (() => {
              const byMonth = {};
              data.by_date.forEach((r) => {
                const mk = String(r.Date || '').slice(0, 7);
                if (mk) byMonth[mk] = (byMonth[mk] || 0) + (r.TotalErrors || 0);
              });
              const rows = Object.keys(byMonth).sort()
                .map((mk) => ({ Month: dayjs(`${mk}-01`).format("MMM 'YY"), TotalErrors: byMonth[mk] }));
              return (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={rows} margin={{ top: 20, right: 16, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="Month" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <RTooltip />
                    <Bar dataKey="TotalErrors" name="Section Errors" fill="#ff4d4f" maxBarSize={60}>
                      <LabelList dataKey="TotalErrors" position="top" style={{ fontSize: 11, fill: '#595959' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              );
            })() : (
              <Typography.Text type="secondary">No data available.</Typography.Text>
            )}
          </Card>
        </Col>

        {/* Errors by section */}
        <Col xs={24}>
          <Card
            className="sticky-table-card"
            title="Errors by Section"
            size="small"
            loading={loading}
            style={{ marginBottom: 0 }}
            extra={
              <Space size={8}>
                <Segmented
                  size="small"
                  value={questionMode}
                  onChange={setQuestionMode}
                  options={[
                    { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                    { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                    { label: 'By Week Pivot',  value: 'week_pivot',  icon: <AppstoreOutlined /> },
                  ]}
                />
                <Button size="small" icon={<DownloadOutlined />} onClick={exportSections}>
                  Export
                </Button>
              </Space>
            }
          >
            {isPivot ? (() => {
              const monthRows = pivotRows();
              const months = [...new Set(monthRows.map(pKey))].sort();
              const mLabels = {};
              months.forEach((mk) => { mLabels[mk] = pLabel(mk); });
              const secMap = {}; const secOrder = [];
              monthRows.forEach((r) => {
                const k = r.Category;
                if (!secMap[k]) { secMap[k] = { _key: k, Category: r.Category }; secOrder.push(k); }
                secMap[k][pKey(r)] = (secMap[k][pKey(r)] || 0) + r.TotalErrors;
              });
              const secPivotData = secOrder.map((k) => secMap[k]);
              const secCols = [
                { title: 'Section', dataIndex: 'Category', key: '__sec', width: 160, ellipsis: true, fixed: 'left' },
                ...months.map((mk) => ({
                  title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                  render: (_, r) => {
                    const v = r[mk];
                    if (!v) return <span style={{ color: '#d9d9d9' }}>—</span>;
                    // The week (Monday) or month this cell counts.
                    const start = isWeek ? dayjs(mk) : dayjs(`${mk}-01`);
                    const period = { from: start.format('YYYY-MM-DD'), label: mLabels[mk] || mk,
                      to: (isWeek ? start.add(6, 'day') : start.endOf('month')).format('YYYY-MM-DD') };
                    return (
                      <Tag color="red" style={{ margin: 0, cursor: 'pointer' }}
                        onClick={() => openDrill(r.Category, 'errors', period)}>{v}</Tag>
                    );
                  },
                })),
              ];
              return (
                <Table rowKey="_key" dataSource={secPivotData} columns={secCols} size="small"
                  pagination={false} sticky={{ offsetHeader: STICKY_OFFSET }}
                  scroll={{ x: 'max-content' }} bordered
                  locale={{ emptyText: 'No data available.' }} />
              );
            })() : (
              <Table
                rowKey={(r) => r.Category}
                dataSource={data?.by_category ?? []}
                size="small"
                pagination={{ defaultPageSize: 25, showSizeChanger: false }}
                sticky={{ offsetHeader: STICKY_OFFSET }}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No data available.' }}
                columns={[
                  { title: 'Section',           dataIndex: 'Category',       key: 'Category',       width: 300, ellipsis: true },
                  { title: 'Section Errors',    dataIndex: 'TotalErrors',    key: 'TotalErrors',    width: 130, align: 'right',
                    render: (v, r) => (
                      <Tag color="red" style={{ cursor: 'pointer' }} onClick={() => openDrill(r.Category, 'errors')}>{v}</Tag>
                    ),
                    sorter: (a, b) => (a.TotalErrors ?? 0) - (b.TotalErrors ?? 0),
                    defaultSortOrder: 'descend',
                  },
                  { title: 'Section Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 145, align: 'right',
                    render: (v, r) => v > 0
                      ? <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => openDrill(r.Category, 'feedbacks')}>{v}</Tag>
                      : <span style={{ color: '#bfbfbf' }}>—</span>,
                    sorter: (a, b) => (a.TotalFeedbacks ?? 0) - (b.TotalFeedbacks ?? 0),
                  },
                  SPACER_COL,
                ]}
              />
            )}
          </Card>
        </Col>

        {/* Errors by question */}
        <Col xs={24}>
          <Card
            className="sticky-table-card"
            title="Errors by Question"
            size="small"
            loading={loading}
            extra={
              <Button size="small" icon={<DownloadOutlined />} onClick={exportQuestions}>
                Export
              </Button>
            }
          >
            {isPivot ? (() => {
              const monthRows = pivotRows();
              const months = [...new Set(monthRows.map(pKey))].sort();
              const mLabels = {};
              months.forEach((mk) => { mLabels[mk] = pLabel(mk); });
              const seen = []; const map = {}; const secOrder = {};
              monthRows.forEach((r) => {
                const k = `${r.Category}||${r.QuestionText}`;
                if (!map[k]) {
                  const idx = seen.length;
                  map[k] = { _key: k, _idx: idx, Category: r.Category, QuestionText: r.QuestionText };
                  seen.push(k);
                  if (!(r.Category in secOrder)) secOrder[r.Category] = idx;
                }
                map[k][pKey(r)] = (map[k][pKey(r)] || 0) + (r.TotalErrors || 0);
              });
              const pivotData = seen
                .map((k) => map[k])
                .sort((a, b) => {
                  const sA = secOrder[a.Category]; const sB = secOrder[b.Category];
                  if (sA !== sB) return sA - sB;
                  return a._idx - b._idx;
                });
              const cols = [
                {
                  title: 'Section', dataIndex: 'Category', key: '__sec', width: 160, ellipsis: true, fixed: 'left',
                  onCell: (r, i) => {
                    const prev = i > 0 ? pivotData[i - 1].Category : null;
                    if (r.Category === prev) return { rowSpan: 0 };
                    let span = 1;
                    while (i + span < pivotData.length && pivotData[i + span].Category === r.Category) span++;
                    return { rowSpan: span };
                  },
                },
                { title: 'Question', dataIndex: 'QuestionText', key: '__q', width: 240, ellipsis: true, fixed: 'left' },
                ...months.map((mk) => ({
                  title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                  render: (_, r) => {
                    const v = r[mk];
                    if (!v) return <span style={{ color: '#d9d9d9' }}>—</span>;
                    // The week (Monday) or month this cell counts.
                    const start = isWeek ? dayjs(mk) : dayjs(`${mk}-01`);
                    const period = { from: start.format('YYYY-MM-DD'), label: mLabels[mk] || mk,
                      to: (isWeek ? start.add(6, 'day') : start.endOf('month')).format('YYYY-MM-DD') };
                    return (
                      <Tag color="red" style={{ margin: 0, cursor: 'pointer' }}
                        onClick={() => openDrill(r.Category, 'errors', period, r.QuestionText)}>{v}</Tag>
                    );
                  },
                })),
              ];
              return (
                <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                  pagination={false} sticky={{ offsetHeader: STICKY_OFFSET }}
                  scroll={{ x: 'max-content' }} bordered
                  locale={{ emptyText: 'No data available.' }}
                  components={{ header: { cell: ResizableTitle } }} />
              );
            })() : (
              <Table
                rowKey={(r) => `${r.Category}-${r.QuestionText}`}
                dataSource={data?.by_question || []}
                columns={questionColumns.map(toResizable)}
                size="small"
                pagination={{ defaultPageSize: 25, showSizeChanger: false }}
                sticky={{ offsetHeader: STICKY_OFFSET }}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No data available.' }}
                components={{ header: { cell: ResizableTitle } }}
              />
            )}
          </Card>
        </Col>
      </Row>
      <Drawer
        open={!!drill}
        onClose={() => setDrill(null)}
        width={820}
        title={drill ? `${drill.section}${drill.question ? ` / ${drill.question}` : ''} — ${drill.kind === 'feedbacks' ? 'Feedbacks' : 'Errors'}`
          + (drill.period ? ` — ${drill.period.label}` : '') : ''}
        destroyOnClose
      >
        <Table
          rowKey="ID"
          size="small"
          loading={!drill?.rows}
          dataSource={drill?.rows || []}
          pagination={{ defaultPageSize: 25, size: 'small', hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          onRow={(r) => ({ onClick: () => window.open(`/errors/${r.ID}`, '_blank', 'noopener'), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'ID', dataIndex: 'ID', key: 'ID', width: 70, sorter: (a, b) => a.ID - b.ID,
              render: (v) => <Typography.Text strong style={{ color: '#1677ff' }}>#{v}</Typography.Text> },
            { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 120,
              sorter: (a, b) => (a.DateOfError || '').localeCompare(b.DateOfError || '') },
            { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 170,
              sorter: (a, b) => (a.AgentName || '').localeCompare(b.AgentName || ''), render: (v) => v || '—' },
            { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 140, render: (v) => v || '—' },
            { title: 'Form', dataIndex: 'FormName', key: 'FormName', width: 200 },
            { title: 'Errors', dataIndex: 'Errors', key: 'Errors', width: 80, align: 'right',
              render: (v) => (v > 0 ? <Tag color="red">{v}</Tag> : '—') },
            { title: 'Feedbacks', dataIndex: 'Feedbacks', key: 'Feedbacks', width: 95, align: 'right',
              render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : '—') },
          ]}
        />
      </Drawer>
    </>
  );
}
